import React, { useEffect, useRef } from 'react';
import { WORLD_CONFIG } from '@astra/shared';

const PALETTE = {
  DARK: '#0f1b26',
  LIGHT: '#f5e8d1',
  TEAL: '#20a5a6',
  ORANGE: '#dd5639',
  DIM: '#4a5a6a',
  PANEL: '#1a2a38',
  YELLOW: '#fbbf24',
  RED: '#ef4444',
  GREEN: '#22c55e',
};

export type RadarEntityType =
  | 'player' | 'asteroid' | 'station' | 'drop' | 'gateway' | 'planet'
  | 'sun' | 'nebula' | 'drone' | 'settlement' | 'ice_asteroid' | 'gas_pocket'
  | 'wreckage' | 'private_station' | 'beacon' | 'hotspot';

export interface RadarEntity {
  id: string;
  type: RadarEntityType;
  x: number;
  y: number;
  name?: string;
  isScanned?: boolean;
  radius?: number;
  data?: Record<string, unknown>;
}

interface RadarMinimapProps {
  playerX: number;
  playerY: number;
  playerRotation: number; // degrees, same convention as ConstellationMapOverlay (0 = up)
  entities: RadarEntity[];
  scannedEntities?: RadarEntity[];
  waypoint?: { x: number; y: number; name: string } | null;
  /** Inner-band radius in world units (what the player sees on screen). Default = VIEW_RADIUS. */
  aoiRadius?: number;
  /** Outer-band radius in world units (scanner max range). Defaults to aoiRadius if no scanner. */
  scannerRange?: number;
  /** Canvas size in pixels. */
  size?: number;
}

// Fraction of the disc radius used for the inner (linear, AOI-aligned) band.
// Outer (1 - AOI_FRAC) is the sqrt-compressed scanner band.
const AOI_FRAC = 0.7;

// Per-type cosmetic config — mirrors ConstellationMapOverlay colors.
const TYPE_STYLE: Record<RadarEntityType, { color: string; shape: 'dot' | 'square' | 'diamond' | 'tri' | 'ring' }> = {
  player:          { color: PALETTE.TEAL,    shape: 'tri'    },
  asteroid:        { color: '#8aaa7a',       shape: 'diamond'},
  station:         { color: '#ffff44',       shape: 'square' },
  private_station: { color: '#22d3ee',       shape: 'diamond'},
  drop:            { color: '#ffdd44',       shape: 'diamond'},
  gateway:         { color: '#00dddd',       shape: 'ring'   },
  planet:          { color: PALETTE.ORANGE,  shape: 'dot'    },
  sun:             { color: '#ffcc00',       shape: 'dot'    },
  nebula:          { color: '#ff66cc',       shape: 'ring'   },
  drone:           { color: '#66ccff',       shape: 'dot'    },
  settlement:      { color: '#9b5cff',       shape: 'ring'   },
  ice_asteroid:    { color: '#88ddff',       shape: 'diamond'},
  gas_pocket:      { color: '#bb99ff',       shape: 'diamond'},
  wreckage:        { color: '#cd7f32',       shape: 'diamond'},
  beacon:          { color: '#ff4466',       shape: 'ring'   },
  hotspot:         { color: '#ff8800',       shape: 'ring'   },
};

export function RadarMinimap({
  playerX,
  playerY,
  playerRotation,
  entities,
  scannedEntities,
  waypoint,
  aoiRadius = WORLD_CONFIG.VIEW_RADIUS,
  scannerRange,
  size = 200,
}: RadarMinimapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Outer range falls back to AOI when no scanner is equipped (or scanner range <= AOI).
  const outerRange = Math.max(aoiRadius, scannerRange ?? aoiRadius);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== size * dpr || canvas.height !== size * dpr) {
      canvas.width = size * dpr;
      canvas.height = size * dpr;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cx = size / 2;
    const cy = size / 2;
    const radius = size / 2 - 2;
    const innerR = radius * AOI_FRAC;

    // Distance → screen-radius mapping (piecewise: linear in AOI, sqrt-compressed outside).
    const distToScreenR = (dist: number): number => {
      if (dist <= aoiRadius) {
        return (dist / aoiRadius) * innerR;
      }
      if (outerRange <= aoiRadius || dist >= outerRange) {
        return radius;
      }
      const t = Math.sqrt((dist - aoiRadius) / (outerRange - aoiRadius));
      return innerR + t * (radius - innerR);
    };

    // Clear
    ctx.clearRect(0, 0, size, size);

    // Disc background
    ctx.fillStyle = `${PALETTE.DARK}ee`;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();

    // Clip to disc for everything below
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.clip();

    // Outer band tint (slightly different from inner — visually distinguishes scanner-only zone)
    if (outerRange > aoiRadius) {
      ctx.fillStyle = `${PALETTE.PANEL}55`;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.arc(cx, cy, innerR, 0, Math.PI * 2, true);
      ctx.fill('evenodd');
    }

    // Inner range marker (1/2 of AOI) — light reference
    ctx.strokeStyle = `${PALETTE.TEAL}22`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy, innerR * 0.5, 0, Math.PI * 2);
    ctx.stroke();

    // AOI horizon ring — dashed, dim teal
    ctx.strokeStyle = `${PALETTE.TEAL}66`;
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);

    // Cross hair axes
    ctx.strokeStyle = `${PALETTE.TEAL}22`;
    ctx.beginPath();
    ctx.moveTo(cx, cy - radius); ctx.lineTo(cx, cy + radius);
    ctx.moveTo(cx - radius, cy); ctx.lineTo(cx + radius, cy);
    ctx.stroke();

    // Project a world point → radar screen.
    const project = (wx: number, wy: number) => {
      const dx = wx - playerX;
      const dy = wy - playerY;
      const dist = Math.hypot(dx, dy);
      if (dist === 0) return { x: cx, y: cy, dist };
      const ang = Math.atan2(dy, dx);
      const r = distToScreenR(dist);
      return { x: cx + Math.cos(ang) * r, y: cy + Math.sin(ang) * r, dist };
    };

    // Build draw set with visibility rules:
    //   - entities (auto-visible / in AOI): drawn if dist <= aoiRadius
    //   - scannedEntities: drawn anywhere up to outerRange (faded). Deduped against entities by id.
    type Drawable = RadarEntity & { _dist: number; _scanned: boolean };
    const seen = new Set<string>();
    const drawables: Drawable[] = [];

    for (const e of entities) {
      if (seen.has(e.id)) continue;
      const dist = Math.hypot(e.x - playerX, e.y - playerY);
      if (dist > aoiRadius) continue;
      seen.add(e.id);
      drawables.push({ ...e, _dist: dist, _scanned: false });
    }

    if (scannedEntities) {
      for (const e of scannedEntities) {
        if (seen.has(e.id)) continue;
        const dist = Math.hypot(e.x - playerX, e.y - playerY);
        if (dist > outerRange) continue;
        seen.add(e.id);
        drawables.push({ ...e, _dist: dist, _scanned: true });
      }
    }

    // Sort: large background entities under small dots.
    drawables.sort((a, b) => {
      const order = (t: RadarEntityType) =>
        t === 'nebula' || t === 'sun' || t === 'planet' ? 0
        : t === 'station' || t === 'private_station' || t === 'gateway' || t === 'settlement' ? 1
        : 2;
      return order(a.type) - order(b.type);
    });

    for (const e of drawables) {
      const { x, y } = project(e.x, e.y);
      const cfg = TYPE_STYLE[e.type];
      if (!cfg) continue;
      drawEntity(ctx, x, y, cfg, e._scanned);
    }

    // Waypoint — uses same projection, clamps to edge if beyond outerRange.
    if (waypoint) {
      const { x: wxs, y: wys, dist } = project(waypoint.x, waypoint.y);
      const onEdge = dist > outerRange;
      ctx.fillStyle = PALETTE.ORANGE;
      ctx.globalAlpha = 0.95;
      ctx.beginPath();
      ctx.moveTo(wxs, wys - 5);
      ctx.lineTo(wxs + 4, wys);
      ctx.lineTo(wxs, wys + 5);
      ctx.lineTo(wxs - 4, wys);
      ctx.closePath();
      ctx.fill();
      if (onEdge) {
        ctx.strokeStyle = PALETTE.ORANGE;
        ctx.globalAlpha = 0.5;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(wxs, wys, 7, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    // Player triangle at center
    const rotRad = (playerRotation - 90) * (Math.PI / 180);
    const shipSize = 6;
    ctx.fillStyle = PALETTE.TEAL;
    ctx.globalAlpha = 1;
    ctx.beginPath();
    ctx.moveTo(
      cx + Math.cos(rotRad) * shipSize,
      cy + Math.sin(rotRad) * shipSize,
    );
    ctx.lineTo(
      cx + Math.cos(rotRad + 2.4) * shipSize * 0.8,
      cy + Math.sin(rotRad + 2.4) * shipSize * 0.8,
    );
    ctx.lineTo(
      cx + Math.cos(rotRad - 2.4) * shipSize * 0.8,
      cy + Math.sin(rotRad - 2.4) * shipSize * 0.8,
    );
    ctx.closePath();
    ctx.fill();

    ctx.restore();

    // Border ring (after restore — not clipped)
    ctx.strokeStyle = `${PALETTE.TEAL}aa`;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.stroke();

    // Labels
    ctx.fillStyle = PALETTE.DIM;
    ctx.font = '8px "JetBrains Mono", monospace';
    ctx.textAlign = 'left';
    ctx.fillText('N', cx - 3, 9);
    // Outer = scanner range
    ctx.textAlign = 'right';
    ctx.fillText(`${formatK(outerRange)}`, size - 4, size - 4);
    // Inner = AOI
    ctx.fillStyle = `${PALETTE.TEAL}99`;
    ctx.textAlign = 'left';
    ctx.fillText(`${formatK(aoiRadius)}`, cx + 4, cy - innerR - 2);
  }, [playerX, playerY, playerRotation, entities, scannedEntities, waypoint, aoiRadius, outerRange, size]);

  return (
    <div
      style={{
        position: 'absolute',
        right: '20px',
        bottom: '20px',
        width: `${size}px`,
        height: `${size}px`,
        pointerEvents: 'none',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          display: 'block',
        }}
      />
    </div>
  );
}

function formatK(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return `${Math.round(n)}`;
}

function drawEntity(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  cfg: { color: string; shape: 'dot' | 'square' | 'diamond' | 'tri' | 'ring' },
  scanned: boolean,
) {
  ctx.fillStyle = cfg.color;
  ctx.strokeStyle = cfg.color;
  ctx.globalAlpha = scanned ? 0.65 : 0.95;
  ctx.lineWidth = 1;

  switch (cfg.shape) {
    case 'dot': {
      const r = 2.5;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'square': {
      const s = 3;
      ctx.fillRect(x - s, y - s, s * 2, s * 2);
      break;
    }
    case 'diamond': {
      const s = 3;
      ctx.beginPath();
      ctx.moveTo(x, y - s);
      ctx.lineTo(x + s, y);
      ctx.lineTo(x, y + s);
      ctx.lineTo(x - s, y);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'tri': {
      const s = 4;
      ctx.beginPath();
      ctx.moveTo(x, y - s);
      ctx.lineTo(x + s * 0.7, y + s * 0.5);
      ctx.lineTo(x - s * 0.7, y + s * 0.5);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'ring': {
      const r = 4;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x, y, 1.5, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
  }

  ctx.globalAlpha = 1;
}
